import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import {
	ChurnInsightsResult,
	FirstDays,
	FlowDirection,
	JourneyAttribute,
	JourneyFilterState,
	JourneyFlowLink,
	JourneyFlowResult,
	JourneyInteraction,
	JourneyMeta,
} from '../../../../models/journey-explorer/journey-explorer';
import { CHURN_INSIGHTS_ENABLED, COLORS, FlowSettings, GoalInfo, formatCompact } from '../journey-explorer-utils';
import { BAR_WIDTH, LABEL_WIDTH, MIN_ROW, buildFlowLayout, isInteractionNode, stepTitle } from '../journey-flow-layout';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import FlowStory from './flow-story';
import FlowNodePanel from './flow-node-panel';
import FlowLinkPanel from './flow-link-panel';
import ChurnInsightsPanel from './churn-insights-panel';
import { DrawerView } from './explore-drawer';
import { LoadingPill } from './loading-indicator';

const MAX_PRESETS = 4;
const COMPARE_BAR = (BAR_WIDTH - 1) / 2;

interface FlowPreset {
	question: string;
	direction: FlowDirection;
	anchor: string;
}

type FlowSelection = { kind: 'node'; key: string } | { kind: 'link'; source: string; target: string };

interface JourneyFlowProps {
	meta: JourneyMeta;
	filters: JourneyFilterState;
	lookup: Map<string, InteractionInfo>;
	flow: JourneyFlowResult | null;
	insights: ChurnInsightsResult | null;
	interactions: JourneyInteraction[];
	goal: GoalInfo | null;
	settings: FlowSettings;
	firstDays: FirstDays | null;
	currency: string;
	loading: boolean;
	insightsLoading: boolean;
	onFlowChange: (patch: Partial<FlowSettings>) => void;
	onFirstDaysChange: (firstDays: FirstDays | null) => void;
	onOpenDrawer: (view: DrawerView) => void;
	onKeep: (attribute: JourneyAttribute, value: string) => void;
	compareLabels: [string, string] | null;
}

const buildPresets = (
	lookup: Map<string, InteractionInfo>,
	interactions: JourneyInteraction[],
	goal: GoalInfo | null
): FlowPreset[] => {
	const presets: FlowPreset[] = [];
	const label = (name: string) => resolveInfo(lookup, name).label;
	const add = (direction: FlowDirection, anchor: string, question: string) => {
		if (!presets.some((preset) => preset.direction === direction && preset.anchor === anchor)) {
			presets.push({ direction, anchor, question });
		}
	};
	const ranked = [...interactions]
		.filter((interaction) => interaction.name !== goal?.name)
		.sort((a, b) => b.customers - a.customers);
	const [busiest, second] = ranked;

	if (goal && interactions.some((interaction) => interaction.name === goal.name)) {
		add('before', goal.name, `What leads to ${goal.label}?`);
	}
	if (busiest) {
		add('after', busiest.name, `First steps after ${label(busiest.name)}`);
	}
	if (second) {
		add('after', second.name, `What follows ${label(second.name)}?`);
	}
	return presets.slice(0, MAX_PRESETS);
};

const JourneyFlow: FC<JourneyFlowProps> = ({
	meta,
	filters,
	lookup,
	flow,
	insights,
	interactions,
	goal,
	settings,
	firstDays,
	currency,
	loading,
	insightsLoading,
	onFlowChange,
	onFirstDaysChange,
	onOpenDrawer,
	onKeep,
	compareLabels,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [size, setSize] = useState({ w: 900, h: 500 });
	const [selected, setSelected] = useState<FlowSelection | null>(null);
	const [hovered, setHovered] = useState<string | null>(null);
	const [insightsOpen, setInsightsOpen] = useState(false);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		const observer = new ResizeObserver(([entry]) =>
			setSize({ w: entry.contentRect.width, h: entry.contentRect.height })
		);
		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	useEffect(() => setSelected(null), [flow]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && setSelected(null);
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	const layout = useMemo(() => (flow ? buildFlowLayout(flow, lookup, size) : null), [flow, lookup, size]);
	const presets = useMemo(() => buildPresets(lookup, interactions, goal), [lookup, interactions, goal]);
	const clearSignals = goal?.outcome ? 0 : insights?.higher.filter((signal) => signal.clear).length ?? 0;
	const selectedNode = selected?.kind === 'node' ? layout?.nodes.get(selected.key) ?? null : null;
	const selectedLink =
		selected?.kind === 'link'
			? flow?.links.find((link) => link.source === selected.source && link.target === selected.target) ?? null
			: null;
	const isSelectedLink = (link: JourneyFlowLink) =>
		selected?.kind === 'link' && selected.source === link.source && selected.target === link.target;

	const touches = (link: JourneyFlowLink, key: string) => link.source === key || link.target === key;
	const isConnected = (key: string) =>
		!hovered ||
		hovered === key ||
		(flow?.links.some((link) => touches(link, hovered) && touches(link, key)) ?? false);
	const ribbonName = (link: JourneyFlowLink) => {
		const source = layout?.nodes.get(link.source)?.label ?? '';
		const target = layout?.nodes.get(link.target)?.label ?? '';
		const [from, to] = flow?.direction === 'before' ? [target, source] : [source, target];
		return `${from} to ${to}`;
	};
	const ribbonTitle = (link: JourneyFlowLink) =>
		`${ribbonName(link)}: ${link.customers.toLocaleString('en-GB')} customers` +
		(goal ? `, ${link.reached_pct.toFixed(0)}% reached ${goal.label}` : '');

	const explore = (anchor: string, direction: FlowDirection) => onFlowChange({ anchor, direction });
	const toggleInsights = () => {
		if (insightsOpen) {
			setSelected(null);
		}
		setInsightsOpen((open) => !open);
	};

	let message: string | null = null;
	if (!settings.anchor) {
		message = 'Choose an interaction in the sidebar to see what customers do around it.';
	} else if (flow && flow.customers === 0) {
		message = `No customers reached ${
			resolveInfo(lookup, flow.anchor).label
		} with these filters. Remove a filter or widen a date range.`;
	}

	return (
		<div className={styles.flow}>
			<div className={styles.flow_main}>
				{flow && flow.customers > 0 && <FlowStory flow={flow} lookup={lookup} goal={goal} />}
				<div className={styles.flow_toolbar}>
					<span className={styles.flow_try}>Try</span>
					{presets.map((preset) => {
						const active = settings.anchor === preset.anchor && settings.direction === preset.direction;
						return (
							<button
								key={`${preset.direction}:${preset.anchor}`}
								type="button"
								aria-pressed={active}
								className={`${styles.flow_chip} ${active ? styles.flow_chip_active : ''}`}
								onClick={() => onFlowChange({ anchor: preset.anchor, direction: preset.direction })}
							>
								{preset.question}
							</button>
						);
					})}
					<span className={styles.flow_legend}>
						<LoadingPill active={loading} label={compareLabels ? 'Comparing flow' : 'Building flow'} />
						<span>Bar height = customers</span>
						{compareLabels && (
							<span className={styles.flow_legend_item}>
								<span className={styles.compare_a}>A {compareLabels[0]}</span>
								<span className={styles.compare_b}>B {compareLabels[1]}</span>
							</span>
						)}
						{goal && (
							<span className={styles.flow_legend_item}>
								<span className={styles.flow_legend_swatch} />
								green share = reached {goal.label}
							</span>
						)}
						{CHURN_INSIGHTS_ENABLED && (
							<button
								type="button"
								aria-pressed={insightsOpen}
								className={`${styles.insights_toggle} ${
									insightsOpen ? styles.insights_toggle_active : ''
								}`}
								onClick={toggleInsights}
							>
								<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
									<rect x="1" y="6" width="2" height="5" rx="1" fill="currentColor" />
									<rect x="5" y="3" width="2" height="8" rx="1" fill="currentColor" />
									<rect x="9" y="1" width="2" height="10" rx="1" fill="currentColor" />
								</svg>
								{insightsOpen ? 'Hide churn insights' : 'Churn insights'}
								{!insightsOpen && clearSignals > 0 && (
									<span className={styles.insights_badge}>{clearSignals}</span>
								)}
							</button>
						)}
					</span>
				</div>
				<div
					ref={containerRef}
					className={`${styles.flow_canvas} ${loading && flow ? styles.loading : ''}`}
					onClick={() => setSelected(null)}
				>
					{layout && flow && flow.customers > 0 && (
						<svg width={layout.width} height={layout.height} className={styles.flow_svg}>
							{layout.columns.map((column) => (
								<text
									key={column.step}
									x={column.x}
									y={20}
									fontSize={11}
									fontWeight={500}
									fill={column.step === 0 || column.step > flow.steps ? COLORS.text : COLORS.dim}
								>
									{stepTitle(column.step, flow)}
								</text>
							))}
							{layout.ribbons.map(({ link, path }) => {
								const linked = hovered !== null && touches(link, hovered);
								const active = isSelectedLink(link);
								let opacity = 0.32;
								if (active) {
									opacity = 0.45;
								} else if (hovered) {
									opacity = linked ? 0.3 : 0.08;
								}
								let fill = linked ? COLORS.muted : COLORS.line;
								if (active) {
									fill = COLORS.accent;
								}
								return (
									<path
										key={`${link.source}|${link.target}`}
										d={path}
										fill={fill}
										opacity={opacity}
										className={styles.flow_ribbon}
										onClick={(event) => {
											event.stopPropagation();
											setSelected({ kind: 'link', source: link.source, target: link.target });
										}}
									>
										<title>{ribbonTitle(link)}</title>
									</path>
								);
							})}
							{Array.from(layout.nodes.values()).map(({ node, label, color, x, y, h }) => {
								const goalHeight = h * (node.customers ? node.reached / node.customers : 0);
								const labelY = y + Math.min(h / 2, 14);
								const real = isInteractionNode(node);
								return (
									<g
										key={node.key}
										className={styles.flow_node}
										opacity={isConnected(node.key) ? 1 : 0.35}
										onMouseEnter={() => setHovered(node.key)}
										onMouseLeave={() => setHovered(null)}
										onClick={(event) => {
											event.stopPropagation();
											setSelected({ kind: 'node', key: node.key });
										}}
									>
										<rect
											x={x - 4}
											y={y - 4}
											width={LABEL_WIDTH + BAR_WIDTH + 8}
											height={Math.max(h, MIN_ROW) + 8}
											fill="transparent"
										/>
										{node.a && node.b ? (
											[node.a, node.b].map((side, index) => {
												const sideHeight = node.customers
													? (h * side.customers) / node.customers
													: 0;
												const sideX = x + index * (COMPARE_BAR + 1);
												const sideGoal =
													sideHeight * (side.customers ? side.reached / side.customers : 0);
												return (
													<g key={index}>
														<rect
															x={sideX}
															y={y}
															width={COMPARE_BAR}
															height={Math.max(sideHeight, 1)}
															rx={1.5}
															fill={index === 0 ? COLORS.compareA : COLORS.compareB}
															opacity={0.85}
														/>
														<rect
															x={sideX}
															y={y + sideHeight - sideGoal}
															width={COMPARE_BAR}
															height={sideGoal}
															rx={1.5}
															fill={COLORS.goal}
														/>
													</g>
												);
											})
										) : (
											<>
												<rect
													x={x}
													y={y}
													width={BAR_WIDTH}
													height={h}
													rx={2}
													fill={color}
													opacity={real ? 0.9 : 0.55}
												/>
												<rect
													x={x}
													y={y + h - goalHeight}
													width={BAR_WIDTH}
													height={goalHeight}
													rx={2}
													fill={COLORS.goal}
													opacity={0.85}
												/>
											</>
										)}
										{selected?.kind === 'node' && selected.key === node.key && (
											<rect
												x={x - 3}
												y={y - 3}
												width={BAR_WIDTH + 6}
												height={h + 6}
												rx={4}
												fill="none"
												stroke={COLORS.text}
												strokeWidth={1.5}
											/>
										)}
										<text
											x={x + BAR_WIDTH + 8}
											y={labelY}
											fontSize={12}
											fontWeight={600}
											fill={real ? COLORS.text : COLORS.muted}
											dominantBaseline="middle"
										>
											{label}
										</text>
										<text
											x={x + BAR_WIDTH + 8}
											y={labelY + 15}
											fontSize={11}
											dominantBaseline="middle"
										>
											{node.a && node.b ? (
												<>
													<tspan fill={COLORS.compareA} fontWeight={700}>
														A{' '}
													</tspan>
													<tspan fill={COLORS.muted}>
														{formatCompact(node.a.customers)}{' '}
													</tspan>
													<tspan fill={COLORS.goal} fontWeight={600}>
														{goal ? `${node.a.reached_pct.toFixed(0)}%` : ''}
													</tspan>
													<tspan fill={COLORS.compareB} fontWeight={700}>
														{'   B '}
													</tspan>
													<tspan fill={COLORS.muted}>
														{formatCompact(node.b.customers)}{' '}
													</tspan>
													<tspan fill={COLORS.goal} fontWeight={600}>
														{goal ? `${node.b.reached_pct.toFixed(0)}%` : ''}
													</tspan>
												</>
											) : (
												<>
													<tspan fill={COLORS.muted}>{formatCompact(node.customers)}</tspan>
													{goal && (
														<>
															<tspan fill={COLORS.dim}>{'  ·  '}</tspan>
															<tspan fill={COLORS.goal} fontWeight={600}>
																{node.reached_pct.toFixed(0)}%
															</tspan>
														</>
													)}
												</>
											)}
										</text>
									</g>
								);
							})}
						</svg>
					)}
					{message && <div className={styles.empty_state}>{message}</div>}
				</div>
			</div>
			{(insightsOpen || selectedNode || selectedLink) && (
				<aside className={styles.flow_panel}>
					{selectedNode && flow && (
						<FlowNodePanel
							key={selectedNode.node.key}
							flow={flow}
							selected={selectedNode}
							lookup={lookup}
							goal={goal}
							meta={meta}
							filters={filters}
							onClose={() => setSelected(null)}
							onExplore={explore}
							onOpenDrawer={onOpenDrawer}
							onKeep={onKeep}
							compareLabels={compareLabels}
						/>
					)}
					{selectedLink && flow && (
						<FlowLinkPanel
							key={`${selectedLink.source}|${selectedLink.target}`}
							flow={flow}
							link={selectedLink}
							title={ribbonName(selectedLink)}
							goal={goal}
							meta={meta}
							filters={filters}
							onClose={() => setSelected(null)}
							onOpenDrawer={onOpenDrawer}
							onKeep={onKeep}
							compareLabels={compareLabels}
						/>
					)}
					{!selectedNode && !selectedLink && (
						<ChurnInsightsPanel
							insights={insights}
							loading={insightsLoading}
							lookup={lookup}
							churnEvents={[]}
							churnStatus={null}
							firstDays={firstDays}
							anchor={settings.anchor}
							currency={currency}
							onFirstDaysChange={onFirstDaysChange}
							onSelect={(name) => explore(name, 'after')}
							onClose={() => setInsightsOpen(false)}
						/>
					)}
				</aside>
			)}
		</div>
	);
};

export default JourneyFlow;
